import type { SolarProposalDesignSectionDto } from "~/api/dto/solar-proposal-design.dto"
import type { SolarProposalEquipmentSectionDto } from "~/api/dto/solar-proposal-equipment.dto"
import type { SolarProposalImpactSectionDto } from "~/api/dto/solar-proposal-impact.dto"
import type { SolarProposalSavingsSectionDto } from "~/api/dto/solar-proposal-savings.dto"

import { toSolarComponents } from "./solar-proposal-equipment"
import { toSolarImpact } from "./solar-proposal-impact"

export const SOLAR_SECTION_LINKS = [
  { id: `home`, label: `Your home` },
  { id: `system`, label: `The system` },
  { id: `components`, label: `Components` },
  { id: `savings`, label: `Savings` },
  { id: `ai-compute`, label: `Compute node` },
  { id: `investment`, label: `Investment` },
  { id: `financing`, label: `Financing` },
  { id: `powur-care`, label: `Powur Care` },
  { id: `next`, label: `Next steps` },
  { id: `environment`, label: `Impact` },
  { id: `homeowner-app`, label: `Homeowner app` },
] as const

export type SolarSectionLink = (typeof SOLAR_SECTION_LINKS)[number]

/**
 * The section rail's links for a real proposal: only sections on the page.
 * Until a read answers, its section is a notice that still carries the
 * section's anchor, so the link stays; `home` arrives with the design itself,
 * `components` leaves when the equipment read has no slide to show,
 * `savings` leaves when the savings read has no Brochure to show, or is
 *   `null` because the page withholds the money,
 * `ai-compute` shows only with a selected AI compute node,
 * `investment` is always there: the pricing read answers with figures, an
 *   empty state or a notice, all under the section's anchor,
 * `powur-care` shows only when the active design includes Powur Care,
 * `next` is static copy, so it is always there,
 * `environment` leaves when the impact read has no card to show, and
 * `homeowner-app` is static, so it is always there.
 */
export const solarSectionLinks = ({
  design,
  equipment,
  savings,
  powurCare,
  impact,
  aiCompute = false,
}: {
  readonly design: SolarProposalDesignSectionDto | undefined
  readonly equipment: SolarProposalEquipmentSectionDto | undefined
  readonly savings: SolarProposalSavingsSectionDto | null | undefined
  readonly powurCare: boolean
  readonly impact: SolarProposalImpactSectionDto | undefined
  /** A selected AI compute node: its section, and a slide in the carousel. */
  readonly aiCompute?: boolean
}): readonly SolarSectionLink[] => {
  const shown = new Set<SolarSectionLink[`id`]>(
    design?.design ? [`home`, `system`] : [`system`],
  )
  if (
    !equipment ||
    aiCompute ||
    (equipment.equipment && toSolarComponents(equipment.equipment).length > 0)
  ) {
    shown.add(`components`)
  }
  if (savings === undefined || savings?.savings) {
    shown.add(`savings`)
  }
  if (aiCompute) {
    shown.add(`ai-compute`)
  }
  shown.add(`investment`)
  if (powurCare) {
    shown.add(`powur-care`)
  }
  shown.add(`next`)
  if (
    !impact ||
    (impact.impact && toSolarImpact(impact.impact).stats.length > 0)
  ) {
    shown.add(`environment`)
  }
  shown.add(`homeowner-app`)
  return SOLAR_SECTION_LINKS.filter((link) => shown.has(link.id))
}
